import { useEffect, useMemo, useRef, useState } from "react";

import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper, { PaperProps } from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { Tag } from "services/types/tags.type";

type TagsAutocompleteProps = {
  loading?: boolean;
  open: boolean;
  onSelect: (tag: Tag | null) => void;
  onClose: () => void;
  anchorEl: any;
  options: Tag[];
  existingTagIds?: Set<number>;
  /** Called on every input keystroke. Parent owns debounce + refetch. */
  onSearchChange?: (search: string) => void;
  /** True when server returned a full page; shows "type to search" hint. */
  isAtCap?: boolean;
};

const StyledPopover = styled(Popover)(({ theme }) => ({
  "&": {
    // z-index must be above MUI Modal (1300) and task modals (1400-1402)
    // to ensure tag autocomplete renders above when used inside modals/dialogs
    zIndex: 1403
  },
  [`& .${autocompleteClasses.paper}`]: {
    marginTop: "9px"
  }
}));

const paperSx = {
  px: 1.5,
  py: 1,
  border: "solid 1px #ccc",
  overflow: "visible",
  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
  "& .MuiAvatar-root": {
    width: 32,
    height: 32,
    ml: -0.5,
    mr: 1
  }
};

export default function TagsAutocomplete({
  loading,
  options,
  open,
  onClose,
  onSelect,
  anchorEl,
  existingTagIds,
  onSearchChange,
  isAtCap
}: TagsAutocompleteProps) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) {
      document.getElementById("tag-search-input")?.focus();
    }
  }, [ready]);

  const handleClose = () => {
    setReady(false);
    onClose();
  };

  // Render the "type to search" hint as a footer of the Autocomplete's
  // listbox Paper. Rendering it as a sibling of the Autocomplete inside the
  // Popover would put it visually beneath the listbox (the listbox's Popper
  // renders absolutely with marginTop:9px from the input bottom, occluding
  // anything in static flow at the same y).
  //
  // Stable reference (empty deps + ref): MUI compares PaperComponent by
  // referential identity. A new function on every `isAtCap` flip would
  // unmount the Paper subtree (losing scroll, flashing the listbox). The
  // ref keeps the latest value readable at render time without changing
  // the component identity.
  const isAtCapRef = useRef(isAtCap);
  isAtCapRef.current = isAtCap;
  const PaperComponent = useMemo(() => {
    const Component = ({ children, ...rest }: PaperProps) => (
      <Paper {...rest}>
        {children}
        {isAtCapRef.current && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              px: 1.5,
              py: 0.75,
              borderTop: "1px solid",
              borderColor: "divider"
            }}
          >
            Type to search for more tags
          </Typography>
        )}
      </Paper>
    );
    Component.displayName = "TagsAutocompleteListboxPaper";
    return Component;
  }, []);

  return (
    <StyledPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      PaperProps={{
        elevation: 0,
        sx: paperSx
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
    >
      <Autocomplete
        id="tag-search-input"
        loading={loading}
        onOpen={(e) => {
          setReady(true);
        }}
        onClose={() => {
          setReady(false);
        }}
        onChange={(event, value, reason) => {
          if (
            reason === "selectOption" &&
            value &&
            !existingTagIds?.has(value.id)
          ) {
            onSelect(value);
            handleClose();
          }
        }}
        // Server-side ?search= when the parent wires onSearchChange. Bypass
        // MUI's client-side filter so substring matches from the server aren't
        // silently dropped.
        // `reason === "input"` covers user typing. `"reset"` (fires after MUI
        // programmatically clears the input on selection) and `"clear"` (X button)
        // must also reset the parent's search to empty — otherwise re-opening the
        // autocomplete still shows the last server-filtered result set.
        onInputChange={(_event, value, reason) => {
          if (!onSearchChange) return;
          if (reason === "input") {
            onSearchChange(value);
          } else if (reason === "reset" || reason === "clear") {
            onSearchChange("");
          }
        }}
        filterOptions={onSearchChange ? (x) => x : undefined}
        blurOnSelect
        openOnFocus
        options={options}
        sx={{ width: 300 }}
        disablePortal
        PaperComponent={PaperComponent}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => {
          return option.name;
        }}
        getOptionDisabled={(option) => existingTagIds?.has(option.id) ?? false}
        renderOption={(props: object, option: Tag) => {
          const isAlreadyAssigned = existingTagIds?.has(option.id) ?? false;
          return (
            <ListItem
              {...props}
              sx={isAlreadyAssigned ? { cursor: "default" } : undefined}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  width={22}
                  minHeight={22}
                  bgcolor={option.color}
                  borderRadius="2px"
                />
                <ListItemText>{option.name}</ListItemText>
                {isAlreadyAssigned && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.primary",
                      whiteSpace: "nowrap"
                    }}
                  >
                    (already added)
                  </Typography>
                )}
              </Stack>
            </ListItem>
          );
        }}
        renderInput={({ InputLabelProps, InputProps, ...params }) => {
          return (
            <InputBase
              {...InputProps}
              {...params}
              autoFocus
              endAdornment={null}
              placeholder="Select tag"
            />
          );
        }}
      />
    </StyledPopover>
  );
}
